import { Helmet } from 'react-helmet-async'
import React, { useContext, useMemo } from 'react'
import useProminentMetaSearchRate from 'hooks/useProminentMetaSearchRate'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { createOfferStructuredData, SCHEMA_TYPES } from 'components/utils/OfferStructuredData'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useBedbankRates from 'hooks/useBedbankRates'
import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'

interface Props {
  offer: App.BedbankOffer
  filters: App.OfferListFilters
}

function MetaSearchStructuredData(props: Props) {
  const { offer, filters } = props
  const utmBp = useAppSelector(state => state.utm.bp)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const queryParams = useAppSelector((state) => state.router.location.query)
  const geo = useContext(GeoContext)
  const contextCurrency = useContext(CurrencyContext)
  const {
    primaryPrice,
  } = useProminentMetaSearchRate({
    isLoggedIn,
    ruleId: queryParams.ruleId,
    offer,
    filters,
    utmBp,
    priceTotal: Number(queryParams.priceTotal || 0),
    userCountry: queryParams.userCountry
  })
  const offerPageState = useContext(OfferPageStateContext)
  const {
    rooms = initialOfferPageRooms,
  } = offerPageState
  const [{ hotelOnlyRates }] = useBedbankRates(offer?.id, rooms, filters.checkIn as string, filters.checkOut as string)

  const currency = contextCurrency ?? geo.currentCurrency

  const availablePackageRates = useMemo(() =>
    hotelOnlyRates.flatMap(rate => rate.totals).filter(Boolean),
  [hotelOnlyRates])

  const pricingData = useMemo(() => {
    if (!filters.checkIn || !filters.checkOut || !(primaryPrice?.offerPrice > 0)) {
      return {}
    }

    // these values are not visible in the metasearch landing page. But adding it here as requested by Google for their Markup trustability
    const allOffers = availablePackageRates?.length > 0 ? availablePackageRates.map(total => createOfferStructuredData(filters.checkIn as string, filters.checkOut as string, {
      propertyFees: total?.propertyFees,
      offerPrice: total?.total || 0,
      offerPriceBeforeTax: total?.exclusive,
      taxesAndFees: total?.taxesAndFees
    }, currency)) : []

    return {
      makesOffer: [createOfferStructuredData(filters.checkIn, filters.checkOut, primaryPrice, currency), ...allOffers],
    }
  }, [filters.checkIn, filters.checkOut, primaryPrice, currency, availablePackageRates])

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': SCHEMA_TYPES.HOTEL,
    name: offer.name,
    identifier: offer.id,
    description: offer.metaDescription,
    address: {
      '@type': SCHEMA_TYPES.POSTAL_ADDRESS,
      addressCountry: offer.property.address.countryCode,
      addressRegion: offer.property.address.stateProvinceName ?? offer.property.address.countryName,
      addressLocality: offer.property.address.city,
      postalCode: offer.property.address.postalCode,
      streetAddress: offer.property.address.line1 + (offer.property.address.line2 ? ' ' + offer.property.address.line2 : ''),
    },
    telephone: offer.property.phone,
    ...pricingData
  }), [offer, pricingData])

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

export default React.memo(MetaSearchStructuredData)
